/**
 * How a recipe search lays out its results (#847).
 *
 * « change la recherche par catégorie, en premier mes préférés ». A search
 * used to return one grid ranked by text, ties broken by the publisher's stars,
 * so a household's own recipes were mixed into 373 others. Results now open on
 * rows built the way the Recipes home screen is:
 *
 *   1. loved: rated 4 or more by the household (the card's heart, the home
 *      screen's « Vos mieux notées »);
 *   2. yours: the household's saved recipes, newest first, whatever their age;
 *   3. pantry: what the shelf can make, the recipe using the most first;
 *
 * and every other match follows as the usual grid, in relevance order.
 *
 * Each recipe appears once, in the first group that claims it. Rows keep every
 * member: the screen draws the first `ROW_LIMIT` and « Tout voir » the rest.
 *
 * A short result list does not get rows (four rows of one card read as a broken
 * layout), and neither does a search where no row has anything to show. Both
 * get one grid in the same order, so the loved recipe still comes first.
 */

import { PersonalRecipeIndexEntry } from './personalRecipeIndex';

export type SearchRowKey = 'loved' | 'yours' | 'pantry';
export type SearchSectionKey = SearchRowKey | 'rest';

export interface SectionedEntry {
  recipe: PersonalRecipeIndexEntry;
  section: SearchSectionKey;
}

export interface SearchRow {
  key: SearchRowKey;
  entries: SectionedEntry[];
}

export type GroupedSearchResults =
  | { layout: 'grid'; entries: SectionedEntry[] }
  | { layout: 'rows'; rows: SearchRow[]; rest: SectionedEntry[] };

/** Below this many results, one grid reads better than rows. */
export const MIN_RESULTS_FOR_ROWS = 8;
/** Cards drawn in a row before « Tout voir ». Same as the home shelves. */
export const ROW_LIMIT = 12;
/** The household's rating from which a recipe counts as loved (the card's heart). */
export const LOVED_RATING = 4;

/**
 * A saved copy carries the channel it came through; the shared catalogue says
 * `base`, and the bundled catalogue says nothing. Same reading as `isOfferedTo`.
 */
export function isHouseholdRecipe(recipe: Pick<PersonalRecipeIndexEntry, 'source'>): boolean {
  return !!recipe.source && recipe.source !== 'base';
}

export interface GroupOptions {
  /** The household's 1-5 rating, derived from its verdicts. */
  ratingOf: (recipeId: string) => number | undefined;
  /** The pantry rows the recipe uses; empty or undefined when none. */
  pantryUsesOf: (recipeId: string) => readonly string[] | undefined;
}

/**
 * @param results the matches in relevance order, as the scorer returned them.
 */
export function groupSearchResults(
  results: PersonalRecipeIndexEntry[],
  { ratingOf, pantryUsesOf }: GroupOptions,
): GroupedSearchResults {
  const loved: SectionedEntry[] = [];
  const yours: SectionedEntry[] = [];
  const pantry: Array<SectionedEntry & { uses: number }> = [];
  const rest: SectionedEntry[] = [];

  for (const recipe of results) {
    if ((ratingOf(recipe.id) ?? 0) >= LOVED_RATING) {
      loved.push({ recipe, section: 'loved' });
    } else if (isHouseholdRecipe(recipe)) {
      yours.push({ recipe, section: 'yours' });
    } else {
      const uses = pantryUsesOf(recipe.id)?.length ?? 0;
      if (uses > 0) pantry.push({ recipe, section: 'pantry', uses });
      else rest.push({ recipe, section: 'rest' });
    }
  }

  // Newest first. A recipe with no date goes last; the sort is stable, so ties
  // keep relevance order.
  yours.sort((a, b) => (b.recipe._importedAt ?? '').localeCompare(a.recipe._importedAt ?? ''));
  pantry.sort((a, b) => b.uses - a.uses);
  const pantryEntries = pantry.map(({ recipe, section }) => ({ recipe, section }));

  const rows: SearchRow[] = (
    [
      { key: 'loved', entries: loved },
      { key: 'yours', entries: yours },
      { key: 'pantry', entries: pantryEntries },
    ] as SearchRow[]
  ).filter(row => row.entries.length > 0);

  if (results.length < MIN_RESULTS_FOR_ROWS || rows.length === 0) {
    return { layout: 'grid', entries: [...loved, ...yours, ...pantryEntries, ...rest] };
  }
  return { layout: 'rows', rows, rest };
}
